import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Settings,
  Receipt, FolderOpen, Crown, Briefcase, Shield,
  Monitor, Calculator, ExternalLink, Users,
} from 'lucide-react';
import { currencySymbol } from '../utils/finance';
import RoiSummary from '../components/RoiSummary';
import ClaimsTracker from '../components/ClaimsTracker';
import DocumentUpload from '../components/DocumentUpload';
import LedgerDashboard from '../components/LedgerDashboard';
import LedgerSettings from '../components/LedgerSettings';
import ContactsTab from '../components/ContactsTab';
import { calcCapexTotal, calcOpexTotal } from '../utils/finance';
import { hasPermission } from '../lib/permissions';

// All possible tabs with role-based visibility.
// CAPEX Planner and OPEX Tracker removed in Phase 4 — now handled by the NK Ledger.
const ALL_TABS = [
  { key: 'roi', label: 'ROI Summary', icon: BarChart3, roles: ['owner', 'viewer'] },
  { key: 'claims', label: 'Claims', icon: Receipt, roles: ['owner', 'viewer'] },
  { key: 'documents', label: 'Documents', icon: FolderOpen, roles: ['owner', 'viewer'] },
  { key: 'contacts', label: 'Contacts', icon: Users, roles: ['owner', 'viewer', 'biz_dev', 'accountant'] },
  { key: 'internal-os', label: 'Internal OS', icon: Monitor, roles: ['owner', 'ops_staff', 'content_manager', 'biz_dev', 'trainer'], app: 'internalOS' },
  { key: 'ledger', label: 'Ledger', icon: Calculator, roles: ['owner', 'accountant'] },
  { key: 'settings', label: 'Settings', icon: Settings, roles: ['owner'] },
];

const ALL_CURRENCIES = ['JPY', 'SGD', 'USD', 'EUR', 'GBP', 'THB', 'IDR', 'MVR'];

export default function BusinessDashboard({
  user,
  rolesById,
  getBusiness,
  updateBusiness,
  updateCapex,
  updateOpex,
  updateAssumptions,
  updateClaims,
  updatePettyCash,
  updateDocuments,
  uploadDocument,
  inviteToBusiness,
  getPendingInvites,
  revokeInvite,
  revokeAccess,
  pettyCashBalance,
  pettyCashCurrency,
}) {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('roi');

  const business = getBusiness(id);

  if (!business) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p>Business not found.</p>
          <Link to="/" className="btn btn-primary">Back to list</Link>
        </div>
      </div>
    );
  }

  const userRole = business.roles[user.uid] || 'viewer';
  // Global owner bypass (Kai's email). All other users must have the 'owner' role on the business
  // OR a role that grants the specific permission being checked.
  const isOwner = userRole === 'owner' || hasPermission(business, user, rolesById, 'edit');
  const businessApps = business.apps || {};
  const currencies = business.currencies || ['JPY', 'SGD', 'USD'];
  const [currency, setCurrency] = useState(currencies[0]);

  // Filter tabs by user role and enabled apps
  const visibleTabs = ALL_TABS.filter((tab) => {
    if (!tab.roles.includes(userRole)) return false;
    if (tab.app && !businessApps[tab.app]) return false;
    return true;
  });

  const capexResult = calcCapexTotal(business.capex || [], business.assumptions?.contingency || 0.1, business.assumptions, currency);
  const opexResult = calcOpexTotal(business.opex || []);

  const handleCapexChange = (newCapex) => {
    if (isOwner) updateCapex(id, newCapex);
  };

  const handleOpexChange = (newOpex) => {
    if (isOwner) updateOpex(id, newOpex);
  };

  const handleAssumptionsChange = (newAssumptions) => {
    if (isOwner) updateAssumptions(id, newAssumptions);
  };

  const handleClaimsChange = (newClaims) => {
    if (isOwner) updateClaims(id, newClaims);
  };

  const handlePettyCashChange = (newPettyCash) => {
    if (isOwner) updatePettyCash(id, newPettyCash);
  };

  const handleDocumentsChange = (newDocs) => {
    if (isOwner) updateDocuments(id, newDocs);
  };

  const handleAddClaimFromReceipt = (claimData) => {
    const claims = business.claims || [];
    const newClaim = {
      id: `claim-${Date.now()}`,
      ...claimData,
      status: 'pending',
      receiptUrl: null,
      createdAt: new Date().toISOString(),
    };
    updateClaims(id, [...claims, newClaim]);
    setActiveTab('claims');
  };

  return (
    <div className="page-container">
      <header className="dashboard-header">
        <div className="header-left">
          <Link to="/" className="btn btn-ghost back-btn">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="business-title">
              {business.name}{' '}
              {business.ledgerEntityId
                ? <span style={{ color: '#6b6b6b', fontWeight: 400, fontSize: '0.7em' }}>({business.ledgerEntityId})</span>
                : <span style={{ color: '#9b9b97', fontWeight: 400, fontStyle: 'italic', fontSize: '0.7em' }}>(no id)</span>}
            </h1>
            <div className="business-title-meta">
              <span className="business-type-badge">{business.type}</span>
              {!business.isProject && business.ownershipPct != null && (
                <span className="ownership-badge-header">{business.ownershipPct}% owned</span>
              )}
              {business.isProject && (
                <span className="project-badge">Project</span>
              )}
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="currency-toggle">
            {currencies.map((c) => (
              <button
                key={c}
                className={`currency-btn ${currency === c ? 'active' : ''}`}
                onClick={() => setCurrency(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </header>

      <nav className="tab-nav">
        {visibleTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`tab-btn ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={18} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <main className="dashboard-main">
        {activeTab === 'roi' && (
          <RoiSummary
            business={business}
            capexResult={capexResult}
            opexResult={opexResult}
            currency={currency}
            isOwner={isOwner}
            onAssumptionsChange={handleAssumptionsChange}
          />
        )}
        {activeTab === 'claims' && (
          <ClaimsTracker
            business={business}
            isOwner={isOwner}
            onClaimsChange={handleClaimsChange}
            onPettyCashChange={handlePettyCashChange}
          />
        )}
        {activeTab === 'documents' && (
          <DocumentUpload
            business={business}
            isOwner={isOwner}
            onUpload={uploadDocument}
            onDocumentsChange={handleDocumentsChange}
            onAddClaim={handleAddClaimFromReceipt}
          />
        )}
        {activeTab === 'contacts' && (
          <ContactsTab businessId={id} isOwner={isOwner} />
        )}
        {activeTab === 'internal-os' && (
          <SubAppLauncher
            title="Internal OS"
            description="Operations management, scheduling, trainers, content, and bookkeeping"
            appUrl={business.appUrls?.internalOS}
            businessId={id}
            userRole={userRole}
          />
        )}
        {activeTab === 'ledger' && (
          <LedgerDashboard business={business} />
        )}
        {activeTab === 'settings' && (
          <>
            <BusinessSettings
              business={business}
              isOwner={isOwner}
              onUpdate={(updates) => isOwner && updateBusiness(id, updates)}
              currencies={currencies}
              setCurrency={setCurrency}
              currency={currency}
            />
            {isOwner && <LedgerSettings business={business} />}
          </>
        )}
      </main>
    </div>
  );
}

function SubAppLauncher({ title, description, appUrl, businessId, userRole }) {
  const handleLaunch = () => {
    // Store auth context for the sub-app
    sessionStorage.setItem('nk_hub_context', JSON.stringify({
      businessId,
      role: userRole,
      returnUrl: window.location.href,
      timestamp: Date.now(),
    }));

    if (appUrl) {
      window.open(`${appUrl}?business=${businessId}&role=${userRole}`, '_blank');
    }
  };

  return (
    <div className="subapp-launcher">
      <div className="subapp-card">
        {title === 'Internal OS' ? <Monitor size={40} /> : <Calculator size={40} />}
        <h3>{title}</h3>
        <p>{description}</p>
        {appUrl ? (
          <button className="btn btn-primary" onClick={handleLaunch}>
            <ExternalLink size={16} /> Open {title}
          </button>
        ) : (
          <div className="subapp-not-configured">
            <p className="subapp-hint">App URL not configured. Set it in Settings &gt; Sub-Apps.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function BusinessSettings({ business, isOwner, onUpdate, currencies, setCurrency, currency }) {
  const [name, setName] = useState(business.name);
  const [type, setType] = useState(business.type);
  const [ownershipPct, setOwnershipPct] = useState(business.ownershipPct ?? 100);
  const [isProject, setIsProject] = useState(business.isProject ?? false);

  const toggleCurrency = (c) => {
    const updated = currencies.includes(c)
      ? currencies.filter((x) => x !== c)
      : [...currencies, c];
    if (updated.length === 0) return;
    onUpdate({ currencies: updated });
    if (!updated.includes(currency)) setCurrency(updated[0]);
  };

  const saveGeneral = () => {
    onUpdate({
      name: name.trim(),
      type: type.trim(),
      isProject,
      ownershipPct: isProject ? 0 : (parseInt(ownershipPct) || 0),
    });
  };

  if (!isOwner) {
    return (
      <div className="settings-panel">
        <div className="settings-header">
          <Settings size={20} />
          <h3>Settings</h3>
          <span className="read-only-badge">Read-only</span>
        </div>
        <div className="settings-section">
          <div className="settings-row">
            <span className="settings-label">Name</span>
            <span>{business.name}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Type</span>
            <span>{business.type}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Classification</span>
            <span>{business.isProject ? 'Project' : `Owned (${business.ownershipPct}%)`}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <Settings size={20} />
        <h3>Settings</h3>
      </div>

      {/* General */}
      <div className="settings-section">
        <h4 className="settings-section-title">General</h4>

        <div className="settings-field">
          <label>Business Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveGeneral}
          />
        </div>

        <div className="settings-field">
          <label>Type</label>
          <input
            type="text"
            value={type}
            onChange={(e) => setType(e.target.value)}
            onBlur={saveGeneral}
            placeholder="e.g. Ski Resort, Gym, Consulting"
          />
        </div>
      </div>

      {/* Classification */}
      <div className="settings-section">
        <h4 className="settings-section-title">Classification</h4>

        <div className="settings-toggle-row">
          <button
            className={`settings-type-btn ${!isProject ? 'active' : ''}`}
            onClick={() => { setIsProject(false); onUpdate({ isProject: false, ownershipPct: ownershipPct || 100 }); }}
          >
            <Crown size={16} />
            <div>
              <span className="type-btn-label">My Business</span>
              <span className="type-btn-desc">Owned entity with equity stake</span>
            </div>
          </button>
          <button
            className={`settings-type-btn ${isProject ? 'active' : ''}`}
            onClick={() => { setIsProject(true); onUpdate({ isProject: true, ownershipPct: 0 }); }}
          >
            <Briefcase size={16} />
            <div>
              <span className="type-btn-label">Project</span>
              <span className="type-btn-desc">Advisory, consulting, or managed</span>
            </div>
          </button>
        </div>

        {!isProject && (
          <div className="settings-field" style={{ marginTop: 16 }}>
            <label>Ownership %</label>
            <div className="ownership-edit-row">
              <input
                type="range"
                min="1"
                max="100"
                value={ownershipPct}
                onChange={(e) => setOwnershipPct(parseInt(e.target.value))}
                onMouseUp={() => onUpdate({ ownershipPct: parseInt(ownershipPct) })}
                onTouchEnd={() => onUpdate({ ownershipPct: parseInt(ownershipPct) })}
                className="ownership-slider"
              />
              <span className="ownership-pct-display">{ownershipPct}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Sub-Apps */}
      <div className="settings-section">
        <h4 className="settings-section-title">Sub-Apps</h4>
        <p className="settings-hint">Enable sub-apps and set their URLs for this business</p>

        <div className="subapp-settings">
          <div className="subapp-setting-row">
            <label className="subapp-toggle">
              <input
                type="checkbox"
                checked={business.apps?.internalOS || false}
                onChange={(e) => onUpdate({ apps: { ...business.apps, internalOS: e.target.checked } })}
              />
              <Monitor size={16} />
              <span>Internal OS</span>
            </label>
            {business.apps?.internalOS && (
              <input
                type="text"
                placeholder="URL (e.g. https://kng-internal.netlify.app)"
                value={business.appUrls?.internalOS || ''}
                onChange={(e) => onUpdate({ appUrls: { ...business.appUrls, internalOS: e.target.value } })}
                className="subapp-url-input"
              />
            )}
          </div>

        </div>
      </div>

      {/* Ledger linkage */}
      <div className="settings-section">
        <h4 className="settings-section-title">Ledger</h4>
        <p className="settings-hint">
          Link this business to a ledger entity on{' '}
          <a href="https://ledger.nexkoins.com" target="_blank" rel="noreferrer">ledger.nexkoins.com</a>.
          Powers the Ledger tab (Dashboard, P&amp;L, Balance Sheet, CapEx).
        </p>
        <div className="settings-field">
          <label>Ledger entity ID</label>
          <input
            type="text"
            value={business.ledgerEntityId || ''}
            onChange={(e) => onUpdate({ ledgerEntityId: e.target.value.trim().toLowerCase() || null })}
            placeholder="e.g. kng, rr, white-manta, ski-myoko"
          />
        </div>
        <LedgerSyncButton business={business} />
      </div>

      {/* Currencies */}
      <div className="settings-section">
        <h4 className="settings-section-title">Currencies</h4>
        <p className="settings-hint">Select which currencies are available for this business</p>
        <div className="settings-currency-grid">
          {['JPY', 'SGD', 'USD', 'EUR', 'GBP', 'THB', 'IDR', 'MVR'].map((c) => (
            <label key={c} className="currency-checkbox">
              <input
                type="checkbox"
                checked={currencies.includes(c)}
                onChange={() => toggleCurrency(c)}
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Sharing - moved to portfolio page Access Management */}
      <div className="settings-section">
        <h4 className="settings-section-title">Sharing & Access</h4>
        <p className="settings-hint">Manage team access from the <strong>Portfolio page &gt; Access Management</strong> section.</p>
        <div style={{ marginTop: 8 }}>
          <span className="access-label" style={{ marginBottom: 6, display: 'block' }}>Current roles</span>
          {Object.entries(business.roles || {}).map(([uid, role]) => (
            <div key={uid} className="access-member-row" style={{ marginBottom: 4 }}>
              <div className="access-member-info">
                <Shield size={14} className={role === 'owner' ? 'role-owner' : 'role-viewer'} />
                <span className="access-member-uid">{uid.slice(0, 16)}...</span>
                <span className={`member-role-badge ${role}`}>{role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// LedgerSyncButton — pushes business metadata to NK Ledger
function LedgerSyncButton({ business }) {
  const [syncing, setSyncing] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const LEDGER_URL = process.env.REACT_APP_LEDGER_URL || 'https://ledger.nexkoins.com';

  const canSync = !!business.ledgerEntityId;

  const sync = async () => {
    if (!canSync) return;
    setSyncing(true);
    setMsg(null);
    try {
      const { auth } = await import('../firebase/config');
      const user = auth.currentUser;
      if (!user) throw new Error('Not signed in');
      const idToken = await user.getIdToken();

      // Map UniHub's business type + family_office_id to Ledger's expected values
      const typeMap = {
        'Gym': 'fitness', 'Fitness': 'fitness',
        'Charter': 'charter-marine', 'Marine': 'charter-marine',
        'Resort': 'hospitality-resort', 'Hospitality': 'hospitality-resort', 'Hotel': 'hospitality-resort',
        'Agency': 'agency-services', 'Consulting': 'agency-services', 'SaaS': 'agency-services',
        'Holding': 'holding', 'Investment': 'holding',
      };
      const inferredType = typeMap[business.type] || 'other';
      const foShortCode = business.family_office_id || 'NK';
      const ownership = foShortCode === 'DP' ? 'dp-owned' : 'nk-owned';

      const body = {
        id: business.ledgerEntityId,
        name: business.name,
        entity_type: inferredType,
        ownership,
        family_office: foShortCode,
        country: business.country || 'SG',
        base_currency: (business.currencies && business.currencies[0]) || 'SGD',
        notes: business.notes || null,
      };

      const res = await fetch(`${LEDGER_URL}/api/entities/sync-from-unihub`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMsg({ ok: true, text: `Synced ${data.action === 'created' ? '(created)' : '(updated)'}` });
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Sync failed' });
    }
    setSyncing(false);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button
        className="btn btn-primary btn-sm"
        onClick={sync}
        disabled={!canSync || syncing}
        title={canSync ? '' : 'Set Ledger entity ID first'}
      >
        {syncing ? 'Syncing…' : 'Sync to Ledger'}
      </button>
      {msg && (
        <span style={{ marginLeft: 10, fontSize: 13, color: msg.ok ? '#5b8b6c' : '#b54a4a' }}>
          {msg.text}
        </span>
      )}
      <p className="settings-hint" style={{ marginTop: 6 }}>
        Creates or updates the Ledger entity matching the <strong>Ledger entity ID</strong> above. Owner only.
      </p>
    </div>
  );
}
